/**
 * Left-hand file tree for the Memory tab (§4.4.4).
 *
 * Small flat list for v1 — templates come from a fixed list; actual
 * files come from the API list. Files present on disk show file size
 * in bytes; templates that don't exist yet show a dashed "unset".
 */
import { FileText, Plus } from 'lucide-react';
import type { MemoryFileInfo } from '@/api/memory';
import { MEMORY_FILE_TEMPLATES } from '@/api/memory';
import { cn } from '@/lib/utils';

interface MemoryFileTreeProps {
  files: MemoryFileInfo[] | undefined;
  selected: string;
  onSelect: (filename: string) => void;
  dirtyFiles?: Set<string>;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function MemoryFileTree({
  files,
  selected,
  onSelect,
  dirtyFiles,
}: MemoryFileTreeProps) {
  // Union templates with whatever's on disk. Templates sort first in
  // the fixed canonical order; extras get appended alphabetically.
  const present = new Set((files ?? []).map((f) => f.filename));
  const ordered: { filename: string; info?: MemoryFileInfo }[] = [];
  for (const name of MEMORY_FILE_TEMPLATES) {
    const info = files?.find((f) => f.filename === name);
    ordered.push({ filename: name, info });
  }
  for (const f of files ?? []) {
    if (!MEMORY_FILE_TEMPLATES.includes(f.filename) && !ordered.find((o) => o.filename === f.filename)) {
      ordered.push({ filename: f.filename, info: f });
    }
  }

  return (
    <aside
      className="flex flex-col border-r bg-muted/20"
      style={{ width: 240, minWidth: 240 }}
      aria-label="Memory files"
    >
      <header className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Files
        </span>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          title="New file (coming soon)"
          aria-label="New file"
          disabled
        >
          <Plus className="h-4 w-4" />
        </button>
      </header>
      <ul className="flex-1 overflow-auto py-1" role="listbox">
        {ordered.map(({ filename, info }) => {
          const exists = present.has(filename);
          const isSelected = selected === filename;
          const dirty = dirtyFiles?.has(filename) ?? false;
          return (
            <li key={filename} role="presentation">
              <button
                type="button"
                onClick={() => onSelect(filename)}
                role="option"
                aria-selected={isSelected}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm',
                  'hover:bg-muted transition-colors',
                  isSelected && 'bg-muted font-medium',
                  !exists && 'text-muted-foreground'
                )}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{filename}</span>
                {dirty && (
                  <span className="h-2 w-2 rounded-full bg-primary" aria-label="Unsaved changes" />
                )}
                {info && (
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {formatBytes(info.size)}
                  </span>
                )}
                {!exists && (
                  <span className="text-[10px] text-muted-foreground italic">new</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
